import ImageService from '@/services/ImageService'
import GLTFService from '@/services/GLTFService'

import AppStore from 'src/store/app'
import { HOME, PATHS, EVENTS } from '@/config/app'
import Env from 'src/config/env'

import AnimationConfig from 'src/config/animation'
import { clamp, map, DEG_TO_RAD } from '@/helpers/MathUtils'
import Pointer from '@/helpers/Pointer'
import EventEmitter from 'src/helpers/EventEmitter' 

import BaseGroup from '@/components/stage/common/base_group' 
  import Communicator from '@/helpers/Communicator'

import gsap from 'gsap'
import dat from 'dat.gui'

const THREE = require('three')
import { SkeletonUtils } from 'three/examples/jsm/utils/SkeletonUtils.js'
 
import vert_morph from '@/components/stage/shaders/dots/vertex_morph.glsl'
import vert_before_morph from '@/components/stage/shaders/dots/vertex_before_morph.glsl' 
 
const Events = {}


class Grid extends BaseGroup {

  constructor(config) {

    super(config)

    this.Events = Events
    Object.assign(this, EventEmitter)

    this.options = {}
    this.base_alpha = Env.mobile ? 0.4 : 0.6 // mouse brightness
    this.tweenObj = {
      x: 1,
      alpha:  this.base_alpha,
      mouseAmt: (Env.mobile || Env.tablet) ? 0 : 1,
      noiseAmt: (Env.mobile || Env.tablet) ? 0.2 : 0.45,
      pulseAmt: 0.,
      pulseRadius: 0
    }

    this.setup()
    this.resize()
  }

  setup() { 
     
    let width =  window.innerWidth 
    let height =  window.innerHeight  

    this.initial = {
      width : width,
      height : height   
    }
 
    this.material = new THREE.PointsMaterial( {  
      transparent: true, 
      size: 2 * Env.pixelRatio,
      blending: THREE.AdditiveBlending,
      map: new THREE.TextureLoader().load( require('@/assets/imgs/dot.png') ) 
    })

    this.material.userData.alpha = { value: this.tweenObj.alpha } 
    this.material.userData.tBubble = { value: this.tBubble } 
    this.material.userData.resolution = { value: new THREE.Vector2(width * Env.pixelRatio, height * Env.pixelRatio) } 
    this.material.userData.mouse = { value: new THREE.Vector2(0,0) } 
    this.material.userData.time = { value:0 } 
    this.material.userData.pulseAmt = { value: this.tweenObj.pulseAmt } 
    this.material.userData.pulseRadius = { value: this.tweenObj.pulseRadius }  
    this.material.userData.pulsePos = { value: 0. } 
    this.material.userData.mouseAmt = { value: this.tweenObj.mouseAmt } 
    this.material.userData.waveAmt = { value: 10 } 
    this.material.userData.noiseAmt = { value: this.tweenObj.noiseAmt } 
    this.material.userData.waveOffset = { value: this.tweenObj.x } 

    this.material.onBeforeCompile = shader => {  
 
      shader.uniforms.alpha = this.material.userData.alpha
      shader.uniforms.tBubble = this.material.userData.tBubble
      shader.uniforms.resolution = this.material.userData.resolution
      shader.uniforms.mouse = this.material.userData.mouse
      shader.uniforms.time = this.material.userData.time
      shader.uniforms.pulseAmt = this.material.userData.pulseAmt
      shader.uniforms.pulseRadius = this.material.userData.pulseRadius
      shader.uniforms.pulsePos = this.material.userData.pulsePos
      shader.uniforms.mouseAmt = this.material.userData.mouseAmt
      shader.uniforms.noiseAmt = this.material.userData.noiseAmt
      shader.uniforms.waveAmt = this.material.userData.waveAmt
      shader.uniforms.waveOffset = this.material.userData.waveOffset
 
      shader.fragmentShader = [  
          'uniform float pulseAmt;',
          'uniform float pulsePos;',
          'uniform float pulseRadius;', 
          'uniform float mouseAmt;',
          'uniform float noiseAmt;',
          'uniform float waveAmt;',
          'uniform float waveOffset;',
          'uniform float alpha;',
          'uniform float time;',
          'uniform sampler2D tBubble;',
          'uniform vec2 resolution;',
          'uniform vec2 mouse;', 
          'varying vec3 vPosition;',
          '',
        ].join( '\n' ) + shader.fragmentShader;
     
      shader.fragmentShader = shader.fragmentShader.replace( 
          '#include <common>', 
          [
            '#include <common>',
            'float circle(vec2 _st, float _radius){', 
              'vec2 dist = _st - vec2(pulsePos, 0.25 );',
              'return 1.-smoothstep(_radius-(_radius*0.05), _radius+(_radius * 0.), dot(dist,dist)*4.);',
            '}'
          ].join( '\n' )
      ); 
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <fog_fragment>',
        [ 
          '#include <fog_fragment>',
  
          'vec2 screenUv = vec2( gl_FragCoord.xy / resolution.xy);',
          'vec2 mouseUv = vec2( gl_FragCoord.xy / mouse.xy );',
          'vec4 bubbleColor = texture2D(tBubble, screenUv);',
          'float bubbleAlpha = clamp(((bubbleColor.r * bubbleColor.g * bubbleColor.b) * 600.), 0., 1.);', 
          'vec2 dist = mouse/resolution - screenUv.xy;',
          'dist.x *= resolution.x/resolution.y;',
          'float mouse_pct = smoothstep(0.1, 0.15, length(dist));', 
          'float mouseAlpha = mix(1., mouse_pct, mouseAmt);',  

 
          'vec2 noiseUv = screenUv;',
          'noiseUv.y -= (time * 0.15);', 
          'vec2 i = floor(noiseUv * 2.);' ,
          'vec2 f = fract(noiseUv * 2.);',
          'float a = fract(sin(dot(i.xy, vec2(12.9898,78.233))) * 43758.5453123);',
          'float b = fract(sin(dot((i + vec2(1.0, 0.0)).xy, vec2(12.9898,78.233))) * 43758.5453123);',
          'float c = fract(sin(dot((i + vec2(0.0, 1.0)).xy, vec2(12.9898,78.233))) * 43758.5453123);', 
          'float d = fract(sin(dot((i + vec2(1.0, 1.0)).xy, vec2(12.9898,78.233))) * 43758.5453123);',  
          'vec2 u = f*f*(3.0-2.0*f);',
          'float noiseAlpha = mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;',
          'noiseAlpha = (0.1 + (0.9 * noiseAlpha));', 

          'float h = waveAmt*(screenUv.x + waveOffset);',
          'float waveAlpha = clamp(h * exp(1.0-h), 0., 1.);', 

          'vec2 pulseUv = vec2(screenUv.x, 0.5 * screenUv.y);',
          'float pulseAlpha = circle(pulseUv, 3. * pulseRadius);',
          'float pulseBlend = pulseAlpha * pulseAmt;',
          
          'float totalAlpha = (1. - bubbleAlpha) * mix(mix(mix(alpha , noiseAmt * noiseAlpha , mouseAlpha) , 1., waveAlpha), 1., pulseBlend);',

          'gl_FragColor *= vec4(gl_FragColor.rgb, totalAlpha);' 
        
        ].join( '\n' )
      )

    }

    //our individual particles
    this.particles = new THREE.Geometry();

    //x y z coordinates
    var pX, pY, pZ;

    //define each particle's position and add it to the system
    let positions = []
    let margin = Env.mobile ? 20 : 28
    let rows = Math.ceil( window.innerHeight / margin )
    let cols = Math.ceil( window.innerWidth / margin )
 
    for (var x = 0, i = 0; i < cols; x+=margin, i++) {
      for (var y = 0, j=0; j < rows; y+=margin, j++) {            
        positions.push({x: x, y: y }) 
      }
    }  
    positions.forEach(position => { 
      let particle = new THREE.Vector3(position.x - (width / 2), position.y - (height / 2) , 0);
      this.particles.vertices.push(particle);
    })


    this.particleSystem = new THREE.Points(this.particles, this.material);
    // particleSystem.sortParticles = true;

    //add it to the scene
    this.add(this.particleSystem);

    Communicator.on(EVENTS.MOUSE_ENTER, this.onMouseEnter)
    Communicator.on(EVENTS.MOUSE_LEAVE, this.onMouseLeave)
  }

  addGUI() {}

  updateGUI () {}


  pointerMove(event){
    this.material.userData.mouse.value.x = (Pointer.x ) * Env.pixelRatio
    this.material.userData.mouse.value.y = (window.innerHeight - Pointer.y ) * Env.pixelRatio
  }

  pointerStart(event){
    if(Env.tablet || Env.mobile){
      gsap.killTweensOf(this.tweenObj, { mouseAmt : true})
      gsap.to(this.tweenObj, 0.3, {
        onUpdate: ()=>{
          this.material.userData.mouseAmt.value = this.tweenObj.mouseAmt
        },
        mouseAmt: 1,
        ease: 'power2.out'
      })
    }
  }

  pointerEnd(event){
    if(Env.tablet || Env.mobile){
      gsap.killTweensOf(this.tweenObj, { mouseAmt : true})
      gsap.to(this.tweenObj, 0.3, {
        onUpdate: ()=>{
          this.material.userData.mouseAmt.value = this.tweenObj.mouseAmt
        },
        mouseAmt: 0,
        ease: 'power2.out'
      })
    }
  }

  onMouseEnter = () => {
    gsap.killTweensOf(this.tweenObj, { alpha : true})
    gsap.to(this.tweenObj, 0.3, {
      onUpdate: ()=>{
        this.material.userData.alpha.value = this.tweenObj.alpha
      },
      alpha: 0.1,
      ease: 'power2.out'
    })
  }

  onMouseLeave = () => { 
    gsap.killTweensOf(this.tweenObj, { alpha : true})
    gsap.to(this.tweenObj, 1, {
      onUpdate: ()=>{
        this.material.userData.alpha.value = this.tweenObj.alpha
      },
      alpha: this.base_alpha,
      ease: 'power2.out'
    })
  }



  // UPDATES -------------------------------------------------------------------


  tick(dt, time ) {
    if(this.material){
      this.material.userData.time.value = time / 1000
    }
  }

  resize() {

    this.width = window.innerWidth
    this.height = window.innerHeight
    this.material.userData.resolution.value = new THREE.Vector2(this.width * Env.pixelRatio, this.height * Env.pixelRatio)
    
    let dotRatio = (this.initial.height / this.initial.width)
    let screenRatio = (this.height / this.width)
    let scale = (screenRatio > dotRatio) ? (this.height / this.initial.height) : ( this.width / this.initial.width )
    this.scale.y = this.scale.x = scale  
  }

  destroy(){}

  loadMainComplete(){}

  loadGameComplete(){}

  enter(){ 
    if((Env.tablet || Env.desktop) && !this.reduced_motion){
      gsap.fromTo(this.tweenObj, 5, {
        x: -1
      }, {
        x: 1,
        onUpdate: ()=>{
          this.material.userData.waveOffset.value = this.tweenObj.x 
        },
        ease: 'power2.out'
      })
    }
  }

  leave(){
   
  }



  // HANDLERS -------------------------------------------------------------------



  // STATES -------------------------------------------------------------------

  /* modal states */

  modalMenu(){ 
    if((Env.tablet || Env.desktop) && !this.reduced_motion){
      gsap.fromTo(this.tweenObj, 5, {
        x: 1
      }, {
        x: -1,
        onUpdate: ()=>{
          this.material.userData.waveOffset.value = this.tweenObj.x 
        },
        ease: 'power2.out'
      })
    }
  }

  modalNone(){  
  }

  pulse( duration, delay, amt = 1 ){

    const home = AppStore.state.home 
    const isHomeModeDefault = ( home.mode === HOME.MODES.DEFAULT ) 

    if(Env.desktop && !this.reduced_motion && isHomeModeDefault){

      gsap.killTweensOf(this.tweenObj, { pulseAmt: true, pulseRadius: true  })
      gsap.fromTo(this.tweenObj, {
        pulseAmt: amt
      }, {
        delay: delay,
        duration: duration,
        pulseAmt: 0,
        onUpdate: () => {
          this.material.userData.pulseAmt.value = this.tweenObj.pulseAmt 
        },
        ease: 'power2.out'
      })
      gsap.fromTo(this.tweenObj, {
        pulseRadius: 0
      }, {
        delay: delay,
        duration: duration,
        pulseRadius: 1,
        onUpdate: () => {
          this.material.userData.pulseRadius.value = this.tweenObj.pulseRadius 
        },
        ease: 'ease.inOut'
      })
    }
  }


  /* page states */

  routeLoading(){ }

  routeHome(){ this.leave() }

  routeOneStar(){ this.enter() }
  routeTwoStar(){ this.enter() }
  routeThreeStar(){ this.enter() }
  routeFourStar(){ this.enter() }
  routeFiveStar(){ this.enter() }


  /* home states */

  welcome(val, prev){ }

  ratingFive(val, prev, dir){
    this.material.userData.pulsePos.value = 0.3 

    const delayMult =  0.95  //dir === -1 ? 0.95 : 0.85 
    const duration = 2
    this.pulse(duration, AnimationConfig.home.section.duration * delayMult, 1)
  }

  ratingFour(val, prev){
    this.material.userData.pulsePos.value = 0.8

    const delay = AnimationConfig.home.section.duration * 0.9
    const duration = 1.5
    this.pulse(duration, delay, 0.8)
  }

  ratingThree(val, prev){
    this.material.userData.pulsePos.value = 0.4

    const delay = AnimationConfig.home.section.duration * 0.85
    const duration = 1.5
    this.pulse(duration, delay, 0.5)
  }

  ratingTwo(val, prev){
    this.material.userData.pulsePos.value = 0.8

    const delay = AnimationConfig.home.section.duration * 0.85
    const duration = 1.5
    this.pulse(duration, delay, 0.3)
  }

  ratingOne(val, prev){
    this.material.userData.pulsePos.value = 0.3

    const delay = AnimationConfig.home.section.duration * 0.85
    const duration = 1.5
    this.pulse(duration, delay, 0.2)
  }

  cta(val, prev){}

}

export default Grid